import moment from 'moment';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';

import linkPropType from '@/custom-prop-types/link';
import { Button } from '@/design-system/atoms/Button';
import { ComplexCardCarousel } from '@/design-system/molecules/ComplexCardCarousel';

const CareerEventsCarousel = ({
    title,
    cta,
    theme,
    limit = 4,
    queryUrl,
    eventDetailsBaseUrl,
    ...props
}) => {
    const [data, setData] = useState(null);

    function formatJsonDate(dateString) {
        // Parse the ISO 8601 date string
        const date = new Date(dateString);

        // Format the date using moment
        const formattedDate = moment(date).format('MMMM D, yyyy');

        return formattedDate;
    }

    const remapData = useCallback(
        (data) => {
            return data.map((item) => ({
                eyebrow: formatJsonDate(item?.StartDate),
                title: item?.Title,
                featuredIcon: item.EventFormat === 'Virtual' ? 'monitor' : 'location-pin',
                featuredContent:
                    item.EventFormat === 'Virtual'
                        ? 'Virtual'
                        : item?.Location.replace(/, United States/g, ''),
                cta: {
                    href: `${eventDetailsBaseUrl}${item?.EventNumber}`,
                },
            }));
        },
        [eventDetailsBaseUrl],
    );

    const filterPastEvents = useCallback((data) => {
        const now = new Date(); // Current time in UTC

        return data.filter((item) => {
            if (!item?.StartDate) return false;
            const eventDate = new Date(item.StartDate);
            return eventDate > now;
        });
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await fetch(queryUrl);

                const json = await response.json();
                const filteredData = filterPastEvents(json.items[0].eventList);
                const remappedData = remapData(filteredData);
                const limitedData = remappedData.slice(0, Number(limit));
                setData(limitedData);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [limit, queryUrl, remapData, filterPastEvents]);

    if ((!data || data.length === 0) && !props.isInEditor) {
        return null;
    }
    return (
        <div {...props}>
            <ComplexCardCarousel
                title={title}
                cta={{ ...cta, buttonStyle: Button.STYLE.PRIMARY }}
                carouselItems={data}
                theme={theme}
                analyticsComponentTitle="career_events_carousel"
            />
        </div>
    );
};

CareerEventsCarousel.propTypes = {
    // The title of the carousel
    title: PropTypes.string,
    // The call to action button
    cta: linkPropType,
    // The theme of the carousel
    theme: PropTypes.oneOf(['light', 'medium']),
    // The number of items to display in the carousel
    limit: PropTypes.number,
    // The URL to fetch the event data
    queryUrl: PropTypes.string,
    // The base URL for the event details
    eventDetailsBaseUrl: PropTypes.string,
};

CareerEventsCarousel.defaultProps = {
    title: 'Upcoming Events',
    theme: 'medium',
    limit: 4,
};

export default CareerEventsCarousel;
